/**
 * Images of the AI interior design landing pages: WebP copies on R2 at a few
 * widths, built and uploaded by scripts/upload-interior-landing-assets.mjs.
 * Rendered with `unoptimized`, so Vercel neither serves nor resizes them.
 *
 * Files never change in place: upload a new version first, then bump this.
 */
export const INTERIOR_ASSET_VERSION = 'v1';

const BASE = `https://media.easy-peasy.ai/landing/interior/${INTERIOR_ASSET_VERSION}`;

/** An image uploaded at several widths: `<base>-<width>.webp`. */
export interface LandingImage {
  /** Path under the version folder without "-<width>.webp", e.g. rooms/kitchen-before. */
  path: string;
  widths: number[];
  /** Width / height. */
  aspect: number;
}

/** Room photos and their redesigns are 1200x896. */
export const ROOM_ASPECT = 1200 / 896;
const ROOM_WIDTHS = [320, 640, 1200];

export const roomImage = (
  slug: string,
  variant: 'before' | 'after-1' | 'after-2' | 'after-3',
): LandingImage => ({
  path: `rooms/${slug}-${variant}`,
  widths: ROOM_WIDTHS,
  aspect: ROOM_ASPECT,
});

/**
 * The outdoor before/after pairs made for these pages (the room-types photos
 * of these rooms are indoor rooms), same size as the room photos.
 */
export const outdoorImage = (
  slug: string,
  variant: 'before' | 'after-1' | 'after-2' | 'after-3',
): LandingImage => ({
  path: `outdoor/${slug}-${variant}`,
  widths: ROOM_WIDTHS,
  aspect: ROOM_ASPECT,
});

/** The JPEG of an outdoor "before" photo, handed to the designer as a sample. */
export const outdoorPhotoUrl = (slug: string) =>
  `${BASE}/outdoor/${slug}-before-1600.jpg`;

/** A style guide's room (the room photo restyled), same size as the rooms. */
export const guideImage = (
  style: string,
  room: 'living-room' | 'bedroom' | 'kitchen',
): LandingImage => ({
  path: `guides/${style}-${room}`,
  widths: ROOM_WIDTHS,
  aspect: ROOM_ASPECT,
});

/** A finished design of the room (square), from room-types/<slug>.jpeg. */
export const roomDesign = (slug: string): LandingImage => ({
  path: `rooms/${slug}-design`,
  widths: [480, 960],
  aspect: 1,
});

export const styleImageUrl = (slug: string) =>
  `${BASE}/styles/${slug}-360.webp`;

export const imageUrl = (image: LandingImage, width?: number) => {
  const widths = [...image.widths].sort((a, b) => a - b);
  const chosen =
    width === undefined
      ? widths[widths.length - 1]
      : (widths.find((w) => w >= width) ?? widths[widths.length - 1]);
  return `${BASE}/${image.path}-${chosen}.webp`;
};

export const imageSrcSet = (image: LandingImage) =>
  image.widths
    .map((width) => `${BASE}/${image.path}-${width}.webp ${width}w`)
    .join(', ');

/**
 * The JPEG copy of an example, for sample photos handed to the designer (its
 * edit path labels every input as PNG for the model, which reads JPEG bytes
 * fine; WebP is untested). Only widths listed under `jpeg` in the script exist.
 */
export const exampleJpegUrl = (name: string, width: number) =>
  `${BASE}/examples/${name}-${width}.jpg`;

export const example = (
  name: string,
  widths: number[],
  aspect: number,
): LandingImage => ({ path: `examples/${name}`, widths, aspect });

/** Real results made with the AI Interior Designer (see the upload script). */
export const EXAMPLES = {
  livingBefore: example('living-before', [320, 640, 1200], ROOM_ASPECT),
  editSofa: example('edit-sofa', [320, 640, 1200], ROOM_ASPECT),
  paintSage: example('paint-sage', [320, 640, 1200], ROOM_ASPECT),
  bathroomBefore: example('bathroom-before', [320, 640, 1200], ROOM_ASPECT),
  paintTerracotta: example('paint-terracotta', [320, 640, 1200], ROOM_ASPECT),
  stagingFurnished: example('staging-furnished', [320, 640], 4 / 3),
  stagingEmpty: example('staging-empty', [320, 640, 1200, 1600], 2752 / 1536),
  stagingStaged: example('staging-staged', [320, 640, 1200, 1600], 2752 / 1536),
  createTropical: example('create-tropical', [320, 640, 1200], 1376 / 768),
} as const;
